// src/hooks/useStatusCounts.js
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDataProvider } from "react-admin";

export const DEFAULT_STATUS_CODES = ["PENDING", "VALIDATED", "REJECTED"];

export default function useStatusCounts(resource, options = {}) {
    const { statuses = DEFAULT_STATUS_CODES } = options;
    const dataProvider = useDataProvider();
    const [counts, setCounts] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const requestId = useRef(0);

    const statusesKey = useMemo(() => statuses.join("|"), [statuses]);

    const fetchCounts = useCallback(async () => {
        const callId = requestId.current + 1;
        requestId.current = callId;

        if (!resource) {
            setCounts({});
            setError(null);
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);
        try {
            const requests = statuses.map(async (status) => {
                const { total = 0 } = await dataProvider.getList(resource, {
                    pagination: { page: 1, perPage: 1 },
                    sort: { field: "id", order: "DESC" },
                    filter: { status },
                });
                return [status, total];
            });

            const entries = await Promise.all(requests);
            if (requestId.current !== callId) {
                return;
            }
            setCounts(
                entries.reduce((acc, [status, total]) => {
                    acc[status] = typeof total === "number" ? total : 0;
                    return acc;
                }, {}),
            );
        } catch (err) {
            console.error(err);
            if (requestId.current !== callId) {
                return;
            }
            setCounts({});
            setError(err);
        } finally {
            if (requestId.current === callId) {
                setLoading(false);
            }
        }
    }, [dataProvider, resource, statusesKey]);

    useEffect(() => {
        fetchCounts();
    }, [fetchCounts]);

    const total = useMemo(
        () => statuses.reduce((sum, status) => sum + (counts[status] ?? 0), 0),
        [counts, statusesKey],
    );

    return {
        counts,
        total,
        loading,
        error,
        refresh: fetchCounts,
    };
}
